import { Component, EventEmitter, Inject, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { NgForm } from '@angular/forms';
import { escape } from 'lodash';
import { AnyComponentData, ConnectionSchemaField, SalesforceSourceComponentData, Schema } from '../../package.models';
import { AppState } from '../../../store';
import { setComponentValidity, updateComponent, updateRawComponent } from '../../store/component.actions';
import { BaseForm, BaseFormInterface } from '../../../common/base/base-form.component';
import { NotifyService } from '../../../common/services/notify.service';
import { ComponentTypeItem } from '../../../constants/component_types';
import { getStep } from '../../../common/helper/get-step.helper';
import { Step } from '../../../common/components/xp-steps.component';
import { SelectPickerTypes } from '../../../common/components/forms/select-picker/select-picker-types.enum';
import { connectionIconUrlByType } from '../../../common/helper/connection-icon-url-by-type.helper';
import { AnyConnection } from '../../../connections/connection.models';
import { ComponentFormTagsService } from '../../../common/services/component-form-tags.service';
import { getConnectionSchemaResponse } from '../../../connections/store/connections.actions';
import { DesignerSchemaFieldI } from '../../models/designer-schema-field.model';

@Component({
  selector: 'salesforce-source-editor',
  template: `
    <div>
      <xp-steps>
        <xp-step [step]="connectionStep">
          <xp-select-picker-editable
            id="connection-picker-component"
            [type]="selectPickerTypes.connection"
            [value]="rawComponent.connection"
            placeholder="Select connection"
            emptyPlaceholder="Connections list is empty"
            (valueChange)="onSelectConnection($event)"
            (createNew)="onCreateNewConnection($event)"
            [params]="{ type: component.connectionTypes }"
            [connectionTypes]="component.connectionTypes.split(',')"
          ></xp-select-picker-editable>
        </xp-step>
        <xp-step [step]="componentBaseStep" (activate)="onBaseStepActivation()">
          <div class="salesforce-source-editor">
            <xp-form-validation type="Xplenty::JobAuthoring::Components::SalesforceSourceComponent">
              <form name="componentForm" novalidate #form="ngForm">
                <div class="alert alert-warning" *ngIf="errorMessage">
                  <button type="button" class="close" (click)="errorMessage = null">
                    <span aria-hidden="true">&times;</span>
                  </button>
                  <span [innerHTML]="errorMessage"></span>
                </div>
                <div class="row">
                  <div class="col-md-12">
                    <label for="access_mode">{{
                      'salesforce-source-editor.form.labels.access_mode' | translate
                    }}</label>
                    <div class="btn-group btn-group-md btn-group-select">
                      <button
                        type="button"
                        class="btn btn-default"
                        value="object"
                        name="access_mode"
                        [ngClass]="{ 'active btn-primary': rawComponent.access_mode === 'object' }"
                        (click)="onValueChange('object', 'access_mode')"
                      >
                        {{ 'salesforce-source-editor.form.labels.object' | translate }}
                      </button>
                      <button
                        type="button"
                        class="btn btn-default"
                        value="query"
                        name="access_mode"
                        [ngClass]="{ 'active btn-primary': rawComponent.access_mode === 'query' }"
                        (click)="onValueChange('query', 'access_mode')"
                      >
                        {{ 'salesforce-source-editor.form.labels.query' | translate }}
                      </button>
                    </div>
                  </div>
                  <div *ngIf="rawComponent.access_mode === 'object'">
                    <div class="col-sm-12">
                      <salesforce-object-picker
                        [rawComponent]="rawComponent"
                        [component]="component"
                        (fieldsLoadStart)="onFieldsLoadStart()"
                        (fieldsLoadEnd)="onFieldsLoadEnd($event)"
                      ></salesforce-object-picker>
                      <xp-form-group>
                        <label for="where_clause"
                          >{{ 'salesforce-source-editor.form.labels.where_clause' | translate }}
                        </label>
                        <code-editor
                          [value]="rawComponent.where_clause"
                          [options]="bodyDataOptions"
                          name="where_clause"
                          (valueChange)="onValueChange($event, 'where_clause')"
                        ></code-editor>
                        <small
                          >Read more about SOQL condition expression syntax
                          <a
                            href="https://developer.salesforce.com/docs/atlas.en-us.soql_sosl.meta/soql_sosl/sforce_api_calls_soql_select_conditionexpression.htm"
                            target="_blank"
                            >here</a
                          >
                        </small>
                      </xp-form-group>
                    </div>
                    <div class="col-sm-6">
                      <xp-form-group [validationDisabled]="true">
                        <label for="source_action">{{
                          'salesforce-source-editor.form.labels.source_action' | translate
                        }}</label>
                        <xp-select
                          class="form-control xp-select"
                          name="source_action"
                          id="source_action"
                          [value]="rawComponent.source_action"
                          [options]="sourceActions"
                          (valueChange)="onValueChange($event, 'source_action')"
                        >
                        </xp-select>
                      </xp-form-group>
                    </div>
                  </div>
                  <div *ngIf="rawComponent.access_mode === 'query'">
                    <xp-form-group class="col-md-12">
                      <label for="query">{{ 'salesforce-source-editor.form.labels.query' | translate }}</label>
                      <code-editor
                        [value]="rawComponent.query"
                        [options]="queryEditorOptions"
                        name="query"
                        (valueChange)="onValueChange($event, 'query')"
                      ></code-editor>
                      <small
                        >Read more about SOQL syntax
                        <a
                          href="https://developer.salesforce.com/docs/atlas.en-us.soql_sosl.meta/soql_sosl/sforce_api_calls_soql.htm"
                          target="_blank"
                          >here</a
                        >
                      </small>
                    </xp-form-group>
                  </div>
                  <div class="form-group">
                    <div class="col-md-12 chunk-size">
                      <xp-input-checkbox
                        [ngModel]="rawComponent.is_chunk_size_enabled"
                        (ngModelChange)="onValueChange($event, 'is_chunk_size_enabled')"
                        [labelText]="'salesforce-source-editor.form.labels.chunk_size_checkbox' | translate"
                        name="is_chunk_size_enabled"
                      ></xp-input-checkbox>
                    </div>
                    <xp-form-group class="col-md-6 chunk-size" *ngIf="rawComponent.is_chunk_size_enabled">
                      <label for="chunk_size">{{
                        'salesforce-source-editor.form.labels.chunk_size' | translate
                      }}</label>
                      <input
                        type="number"
                        class="form-control"
                        name="chunk_size"
                        id="chunk_size"
                        [ngModel]="rawComponent.chunk_size"
                        (ngModelChange)="onValueChange($event, 'chunk_size')"
                        placeholder="{{ 'salesforce-source-editor.form.labels.chunk_size' | translate }}"
                        [disabled]="!rawComponent.is_chunk_size_enabled"
                      />
                      <div class="error-message" *ngIf="!form.controls.chunk_size.valid">
                        <span>{{ 'salesforce-source-editor.form.labels.chunk_size_error' | translate }}</span>
                      </div>
                    </xp-form-group>
                  </div>
                </div>
              </form>
            </xp-form-validation>
          </div>
        </xp-step>
        <xp-step [step]="schemaImporterStep" [isWide]="true">
          <schema-importer
            (fieldsChange)="onFieldsChange($event)"
            [fields]="(rawComponent.schema || {}).fields || []"
            [isConnectionSchemaLoading]="areFieldsLoading"
            [component]="component"
            [rawComponent]="rawComponent"
            (updateValidation)="updateSchemaValidation($event)"
          ></schema-importer>
        </xp-step>
      </xp-steps>
    </div>
  `,
})
export class SalesforceSourceEditorComponent extends BaseForm implements BaseFormInterface, OnChanges {
  @Input() rawComponent: AnyComponentData;
  @Input() component: ComponentTypeItem;
  @Input() parentSchemas: Schema[];
  @Output() formValidationChange = new EventEmitter<boolean>();
  @Output() createConnection = new EventEmitter();
  @ViewChild('form') form: NgForm;
  formName = 'componentForm';
  successMessageText = '';

  selectPickerTypes = SelectPickerTypes;

  isSchemaValid = true;
  isFormValid = true;
  validationChangeSubscription: Subscription;
  schemas = [];
  idOptions = [];
  schemaReady = false;
  areFieldsLoading = false;
  fields = [];
  idFieldErrorMsg = '';
  errorMessage = '';
  isBaseComponentStepActivated = false;

  connectionStep: Step = getStep({ active: true });
  componentBaseStep: Step = getStep({});
  schemaImporterStep: Step = getStep({});

  sourceActions = [
    { text: 'Bulk query', value: 'query' },
    { text: 'Bulk query all', value: 'queryAll' },
  ];
  bodyDataOptions = {
    lineWrapping: true,
    lineNumbers: true,
    mode: 'text/x-sql',
    placeholder: this.translate.instant('salesforce-source-editor.form.placeholders.where_clause'),
  };
  queryEditorOptions = {
    placeholder: 'SELECT field FROM object',
  };

  constructor(
    protected store: Store<AppState>,
    protected notify: NotifyService,
    protected translate: TranslateService,
    private componentFormTagsService: ComponentFormTagsService,
    @Inject('$stateParams') private $stateParams: any,
  ) {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
    this.validationChangeSubscription = this.formValidationChange.subscribe((isFormValid) => {
      this.isFormValid = isFormValid;

      this.componentBaseStep = {
        ...this.componentBaseStep,
        valid: !!this.rawComponent.connection?.id && isFormValid,
        isError: this.isBaseComponentStepActivated && !isFormValid,
        tags: this.componentFormTagsService.getTags(this.rawComponent, this.component),
      };
      this.onValidityChange();
    });

    this.connectionStep = getStep({
      title: this.translate.instant(`component-editor.step-connection.${this.component.type}.closed`),
      activeTitle: this.translate.instant(`component-editor.step-connection.${this.component.type}.active`),
      valid: !!this.rawComponent.connection?.id,
      active: true,
    });

    this.componentBaseStep = getStep({
      title: this.translate.instant(`component-editor.step-editor.${this.component.componentType}.closed`),
      activeTitle: this.translate.instant(`component-editor.step-editor.${this.component.componentType}.active`),
      valid: !!this.rawComponent.connection?.id,
      tags: this.rawComponent.connection?.id
        ? this.componentFormTagsService.getTags(this.rawComponent, this.component)
        : [],
    });

    this.schemaImporterStep = getStep({
      title: this.translate.instant(`component-editor.step-schema.${this.component.componentType}.closed`),
      activeTitle: this.translate.instant(`component-editor.step-schema.${this.component.componentType}.active`),
      valid: !!this.rawComponent.schema?.fields?.length,
      tags: (((this.rawComponent.schema || {}).fields as ConnectionSchemaField[]) || []).map((field) => ({
        name: field.alias,
      })),
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    this.componentBaseStep = {
      ...this.componentBaseStep,
      tags: this.rawComponent.connection?.id
        ? this.componentFormTagsService.getTags(this.rawComponent, this.component)
        : [],
    };

    if (changes.rawComponent?.currentValue?.access_mode !== changes.rawComponent?.previousValue?.access_mode) {
      if ((this.rawComponent as SalesforceSourceComponentData).access_mode === 'object') {
        this.onValueChange(true, 'disableSchemaLoad');
        this.onValueChange(null, 'service_type');
      } else {
        this.onValueChange(false, 'disableSchemaLoad');
        this.onValueChange('file_type', 'service_type');
      }
    }

    if (
      changes.rawComponent?.currentValue?.is_chunk_size_enabled !==
      changes.rawComponent?.previousValue?.is_chunk_size_enabled
    ) {
      if ((this.rawComponent as SalesforceSourceComponentData).is_chunk_size_enabled && !this.rawComponent.chunk_size) {
        this.onValueChange(100000, 'chunk_size');
      }
    }
  }

  onBaseStepActivation() {
    this.isBaseComponentStepActivated = true;
  }

  updateSchemaValidation(isSchemaValid: boolean) {
    this.isSchemaValid = isSchemaValid;

    this.schemaImporterStep = { ...this.schemaImporterStep, valid: this.isSchemaValid, isError: !this.isSchemaValid };
    this.onValidityChange();
  }

  onValidityChange() {
    const isValid = this.isSchemaValid && this.isFormValid;

    this.store.dispatch(setComponentValidity({ isComponentFormValid: isValid }));
  }

  onSelectConnection(connection: AnyConnection) {
    this.store.dispatch(
      updateRawComponent({
        rawComponent: { connection },
      }),
    );
    this.store.dispatch(updateComponent({ component: { connection } }));

    const img = `<img class="tag-icon" src="${connectionIconUrlByType(connection.type)}" alt="${connection.name}" />`;

    this.connectionStep.tags = [
      {
        name: `${img}<b>${escape(connection.name)}</b>`,
      },
    ];

    this.connectionStep = { ...this.connectionStep, valid: true };
  }

  onCreateNewConnection(params) {
    this.createConnection.emit(params);
  }

  onValueChange(value: any, key: string) {
    this.store.dispatch(
      updateRawComponent({
        rawComponent: { [key]: value },
      }),
    );
    this.store.dispatch(updateComponent({ component: { [key]: value } }));
  }

  onFieldsChange(fields: ConnectionSchemaField[]) {
    this.schemaImporterStep = {
      ...this.schemaImporterStep,
      tags: fields.map((field) => ({
        name: field.alias,
      })),
    };

    this.store.dispatch(
      updateRawComponent({
        rawComponent: { schema: { ...this.rawComponent.schema, fields } },
      }),
    );
  }

  onFieldsLoadStart() {
    this.schemaReady = false;
    this.schemaImporterStep.lock = false;
    this.areFieldsLoading = true;
  }

  onFieldsLoadEnd(fields: DesignerSchemaFieldI[]) {
    const destinationSchema: any = {};

    destinationSchema.id = 'salesforce_fields';
    destinationSchema.name = 'salesforce_fields';
    destinationSchema.fields = [];

    this.idOptions = [];

    fields.forEach((field) => {
      destinationSchema.fields.push({
        name: field.name,
      });
      if (field && field.is_keyable) {
        this.idOptions.push({ text: field.name, value: field.name });
      }
    });

    this.schemas = [this.parentSchemas[0], destinationSchema];
    this.fields = fields;

    this.schemaReady = true;
    this.schemaImporterStep.lock = false;

    this.checkUpsertKey(fields);

    this.areFieldsLoading = false;

    this.store.dispatch(
      getConnectionSchemaResponse({
        connectionSchema: fields as ConnectionSchemaField[],
        connectionSchemaData: { data: [], fields: [] },
      }),
    );
  }

  checkUpsertKey(fields) {
    if (this.rawComponent.operation_type === 'upsert' && this.rawComponent.id_field) {
      const upsertKeyRecord = fields.find((field) => field.name === this.rawComponent.id_field);
      if (!upsertKeyRecord) {
        this.idFieldErrorMsg = this.translate.instant('salesforce-destination-editor.form.errors.id_field', {
          field: this.rawComponent.id_field,
        });
        this.rawComponent.id_field = '';
        setTimeout(() => {
          if (this.form.controls.id_field) {
            this.form.controls.id_field.markAsDirty();
          }
        });
      }
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();

    if (this.validationChangeSubscription) {
      this.validationChangeSubscription.unsubscribe();
    }
  }
}
